import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideOktaAuth, withOktaConfig } from '@okta/okta-angular';

import { AuthSessionService } from '@core/auth/services/auth-session.service';
import { httpClientInterceptor } from '@core/interceptors';
import { IdleSessionService } from '@core/services/idle-session.service';
import { ImpersonateUserConfigService } from '@core/services/impersonate-user-config.service';
import { UserDetailsService } from '@core/services/user-details.service';

import { routes } from './app.routes';
import { oktaAuth } from './auth.config';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([httpClientInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideZonelessChangeDetection(),
    provideOktaAuth(withOktaConfig({ oktaAuth })),
    // Preloads session and user config before first render so AI cockpit visibility is ready on refresh.
    provideAppInitializer(async () => {
      const authSession = inject(AuthSessionService);
      const idleSession = inject(IdleSessionService);
      const mainConfig = inject(ImpersonateUserConfigService);
      const userDetails = inject(UserDetailsService);

      try {
        await authSession.loadSession();
        idleSession.start();
        if (!authSession.isAuthenticated() && !authSession.hasImpersonatedSession()) return;
        const effectiveEmail = await authSession.getUserEmail();
        if (!effectiveEmail.trim()) return;
        await Promise.all([
          mainConfig.loadMainConfig(),
          userDetails.loadUserContextByEmail(effectiveEmail),
        ]);
      } catch {
        return;
      }
    }),
  ],
};
